/**
 * useOrderDetails Hook
 * Custom hook for fetching and managing order details
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-004
 */

import { useState, useEffect, useCallback } from 'react';
import { orderService, OrderApiError } from '@/services/commerce/orderService';
import type { OrderDetails } from '@/types/order';

interface UseOrderDetailsReturn {
  order: OrderDetails | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  confirmOrder: (remarks?: string) => Promise<void>;
  cancelOrder: (reason: string) => Promise<void>;
  markReady: () => Promise<void>;
  scheduleDelivery: (deliveryDate: string) => Promise<void>;
}

/**
 * Custom hook to fetch and manage order details
 *
 * @param orderId - Order ID to fetch
 * @returns Order state and action methods
 */
export function useOrderDetails(orderId: number | string): UseOrderDetailsReturn {
  const [order, setOrder] = useState<OrderDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch order details from API
   */
  const fetchOrder = useCallback(async () => {
    if (!orderId) {
      setError('Order ID is required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const numericOrderId = typeof orderId === 'string' ? parseInt(orderId, 10) : orderId;

      if (isNaN(numericOrderId)) {
        throw new Error('Invalid order ID format');
      }

      const data = await orderService.getOrderById(numericOrderId);
      setOrder(data);
    } catch (err) {
      const errorMessage = err instanceof OrderApiError ? err.message : 'Failed to load order details';
      setError(errorMessage);
      console.error('Error fetching order:', err);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  /**
   * Refetch order details (for manual refresh)
   */
  const refetch = useCallback(async () => {
    await fetchOrder();
  }, [fetchOrder]);

  /**
   * Confirm the order
   */
  const confirmOrder = useCallback(
    async (remarks?: string) => {
      if (!order) {
        throw new Error('Order not loaded');
      }

      try {
        const updatedOrder = await orderService.confirmOrder(order.id, remarks);
        setOrder(updatedOrder);
      } catch (err) {
        const errorMessage = err instanceof OrderApiError ? err.message : 'Failed to confirm order';
        throw new Error(errorMessage);
      }
    },
    [order]
  );

  /**
   * Cancel the order
   */
  const cancelOrder = useCallback(
    async (reason: string) => {
      if (!order) {
        throw new Error('Order not loaded');
      }

      try {
        const updatedOrder = await orderService.cancelOrder(order.id, reason);
        setOrder(updatedOrder);
      } catch (err) {
        const errorMessage = err instanceof OrderApiError ? err.message : 'Failed to cancel order';
        throw new Error(errorMessage);
      }
    },
    [order]
  );

  /**
   * Mark order as ready for dispatch
   */
  const markReady = useCallback(async () => {
    if (!order) {
      throw new Error('Order not loaded');
    }

    try {
      const updatedOrder = await orderService.markReady(order.id);
      setOrder(updatedOrder);
    } catch (err) {
      const errorMessage = err instanceof OrderApiError ? err.message : 'Failed to mark order as ready';
      throw new Error(errorMessage);
    }
  }, [order]);

  /**
   * Schedule delivery for the order
   */
  const scheduleDelivery = useCallback(
    async (deliveryDate: string) => {
      if (!order) {
        throw new Error('Order not loaded');
      }

      try {
        const updatedOrder = await orderService.scheduleDelivery(order.id, deliveryDate);
        setOrder(updatedOrder);
      } catch (err) {
        const errorMessage = err instanceof OrderApiError ? err.message : 'Failed to schedule delivery';
        throw new Error(errorMessage);
      }
    },
    [order]
  );

  // Fetch order on mount and when orderId changes
  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  return {
    order,
    loading,
    error,
    refetch,
    confirmOrder,
    cancelOrder,
    markReady,
    scheduleDelivery,
  };
}
